
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";

interface TrainingTypesSelectorProps {
  trainingTypes: string[];
  predefinedTypes: string[];
  onTrainingTypesChange: (types: string[]) => void;
}

// Define training type translations
const trainingTypeTranslations: Record<string, Record<string, string>> = {
  en: {
    "Strength Training": "Strength Training",
    "Functional Training": "Functional Training",
    "HIIT": "HIIT",
    "Cardio": "Cardio",
    "Weight Loss": "Weight Loss",
    "Muscle Gain": "Muscle Gain",
    "Endurance": "Endurance",
    "Flexibility": "Flexibility",
    "Powerlifting": "Powerlifting",
    "Bodybuilding": "Bodybuilding",
    "CrossFit": "CrossFit",
    "Yoga": "Yoga",
    "Pilates": "Pilates",
    "Calisthenics": "Calisthenics",
    "Personal Training": "Personal Training",
    "Musculação": "Strength Training",
    "Treino Funcional": "Functional Training",
    "Perda de Peso": "Weight Loss",
    "Ganho de Massa Muscular": "Muscle Gain",
    "Resistência": "Endurance",
    "Flexibilidade": "Flexibility",
    "Culturismo": "Bodybuilding"
  },
  pt: {
    "Strength Training": "Musculação",
    "Functional Training": "Treino Funcional",
    "HIIT": "HIIT",
    "Cardio": "Cardio",
    "Weight Loss": "Perda de Peso",
    "Muscle Gain": "Ganho de Massa Muscular",
    "Endurance": "Resistência",
    "Flexibility": "Flexibilidade",
    "Powerlifting": "Powerlifting",
    "Bodybuilding": "Culturismo",
    "CrossFit": "CrossFit",
    "Yoga": "Yoga",
    "Pilates": "Pilates",
    "Calisthenics": "Calistenia",
    "Personal Training": "Personal Training",
    "Musculação": "Musculação",
    "Treino Funcional": "Treino Funcional",
    "Perda de Peso": "Perda de Peso",
    "Ganho de Massa Muscular": "Ganho de Massa Muscular",
    "Resistência": "Resistência",
    "Flexibilidade": "Flexibilidade",
    "Culturismo": "Culturismo"
  }
};

export const TrainingTypesSelector = ({
  trainingTypes = [],
  predefinedTypes = [],
  onTrainingTypesChange
}: TrainingTypesSelectorProps) => {
  const { t } = useTranslations("profile");
  const { language } = useLanguage();
  
  // Get the appropriate translation map based on current language
  const translations = trainingTypeTranslations[language as 'en' | 'pt'] || trainingTypeTranslations.en;
  
  const handleToggleType = (type: string) => {
    const isSelected = trainingTypes.includes(type);
    if (isSelected) {
      onTrainingTypesChange(trainingTypes.filter(t => t !== type));
    } else {
      onTrainingTypesChange([...trainingTypes, type]);
    }
  };

  // Ensure arrays are never undefined
  const safeTrainingTypes = trainingTypes || [];
  const safePredefinedTypes = predefinedTypes || [];
  
  return (
    <div className="space-y-4">
      <Label className="text-slate-800 dark:text-gray-200">{t("trainingTypes")}</Label>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {safePredefinedTypes.map(type => (
          <div key={type} className="flex items-center space-x-2">
            <Checkbox 
              id={`type-${type}`} 
              checked={safeTrainingTypes.includes(type)} 
              onCheckedChange={() => handleToggleType(type)} 
              className="dark:border-gray-500 dark:data-[state=checked]:bg-gray-100 dark:data-[state=checked]:text-gray-900"
            />
            <Label htmlFor={`type-${type}`} className="text-sm cursor-pointer text-slate-800 dark:text-gray-200">
              {translations[type] || type}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
};
